/**
 * reset-motion.js — IO bootstrapper for .reset-reveal scroll-reveal utilities.
 * P1 motion foundation · 2026-06-11
 *
 * Size target: <2KB minified.
 * Vanilla JS, no dependencies, defer-loaded.
 *
 * What it does:
 *  - Skips ALL motion setup when prefers-reduced-motion is reduced.
 *    (CSS already renders elements at final state; this ensures no
 *     .is-inview assignment that could accidentally re-enable a transition
 *     if the media query fires mid-session.)
 *  - Observes every .reset-reveal element with IntersectionObserver.
 *  - Adds .is-inview when the element enters the viewport (triggers the
 *    CSS transition defined in reset-motion.css).
 *  - Sets --animation-order on [data-cascade] children inside
 *    .reset-stagger-group containers (matches Dawn's own cascade pattern
 *    from animations.js so both systems coexist without conflicts).
 *  - Wires Shopify design-mode events so newly loaded sections also get
 *    observed (mirrors Dawn's shopify:section:load pattern).
 *  - Does NOT touch Dawn's own scroll-trigger / scroll-trigger--offscreen
 *    classes — fully additive.
 *
 * Dependency note:
 *   Dawn already conditionally loads animations.js (when
 *   settings.animations_reveal_on_scroll is enabled). This file is loaded
 *   unconditionally — it is infrastructure, not a setting toggle. The two
 *   IO observers are independent and will not conflict.
 */

(function () {
  'use strict';

  var REVEAL_CLASS  = 'reset-reveal';
  var INVIEW_CLASS  = 'is-inview';
  var CANCEL_CLASS  = 'reset-reveal--cancel';
  var GROUP_CLASS   = 'reset-stagger-group';

  /* Bail out entirely for users who prefer reduced motion. */
  var motionOK = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /**
   * Assign --animation-order to [data-cascade] children of a stagger group.
   * Called once on init and again on section loads.
   * @param {Element|Document} root
   */
  function assignStaggerOrder(root) {
    var groups = root.getElementsByClassName(GROUP_CLASS);
    Array.prototype.forEach.call(groups, function (group) {
      var children = group.querySelectorAll('.' + REVEAL_CLASS + '[data-cascade]');
      Array.prototype.forEach.call(children, function (el, i) {
        el.style.setProperty('--animation-order', i);
      });
    });
  }

  /**
   * Create and return an IntersectionObserver that adds .is-inview when
   * an element enters the viewport.
   */
  function createObserver() {
    return new IntersectionObserver(
      function (entries, observer) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add(INVIEW_CLASS);
            entry.target.classList.remove(CANCEL_CLASS);
            observer.unobserve(entry.target);
          }
        });
      },
      {
        /* Trigger slightly before the element reaches the visible area
           so the animation is well underway when it hits centre-screen. */
        rootMargin: '0px 0px -48px 0px',
        threshold: 0,
      }
    );
  }

  /**
   * Observe all .reset-reveal elements within a root element.
   * @param {Element|Document} root
   * @param {IntersectionObserver} observer
   * @param {boolean} designMode — when true, reveal immediately (editor UX)
   */
  function initReveal(root, observer, designMode) {
    var els = root.getElementsByClassName(REVEAL_CLASS);
    Array.prototype.forEach.call(els, function (el) {
      if (designMode) {
        /* Theme editor: show content immediately — don't animate. */
        el.classList.add(INVIEW_CLASS);
      } else if (motionOK) {
        observer.observe(el);
      } else {
        /* Reduced-motion: reveal immediately, no observer needed. */
        el.classList.add(INVIEW_CLASS);
      }
    });
  }

  function init() {
    assignStaggerOrder(document);

    var observer = motionOK ? createObserver() : null;

    initReveal(document, observer, false);

    /* Shopify design-mode events (mirrors Dawn animations.js pattern). */
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:section:load', function (e) {
        assignStaggerOrder(e.target);
        initReveal(e.target, observer, true);
      });
      document.addEventListener('shopify:section:reorder', function () {
        /* On reorder, just reveal all — layout is being rearranged anyway. */
        var els = document.getElementsByClassName(REVEAL_CLASS);
        Array.prototype.forEach.call(els, function (el) {
          el.classList.add(INVIEW_CLASS);
        });
      });
    }
  }

  /* Run after DOM is ready. The script tag is `defer` so DOMContentLoaded
     may have already fired; handle both cases. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

}());
